// Use centralized API client for proper authentication handling
import { devicesApi } from './apiClient';


/**
 * Service for handling device-related API interactions with caching
 */
class DeviceService {
  /**
   * Cache storage key for localStorage
   * @private
   */
  static #CACHE_KEY = 'deviceService_cache';

  /**
   * Cache storage
   * @private
   */
  static #cache = {
    devices: null,
    timestamp: null,
    ttl: 5 * 60 * 1000, // 5 minutes cache TTL
  };

  /**
   * Status polling interval
   * @private
   */
  static #statusPollingInterval = null;

  /**
   * Load cache from localStorage on initialization
   * @private
   */
  static #loadCacheFromStorage() {
    try {
      const stored = localStorage.getItem(this.#CACHE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Only load if timestamp is within TTL
        if (parsed.timestamp && (Date.now() - parsed.timestamp) < this.#cache.ttl) {
          this.#cache = parsed;
          console.log('[DeviceService] Loaded cache from localStorage (age:', Math.round((Date.now() - parsed.timestamp) / 1000), 'seconds)');
          return true;
        } else {
          console.log('[DeviceService] Cache in localStorage is expired, clearing...');
          localStorage.removeItem(this.#CACHE_KEY);
        }
      }
    } catch (error) {
      console.error('[DeviceService] Failed to load cache from localStorage:', error);
    }
    return false;
  }

  /**
   * Save cache to localStorage (lightweight version - only essential data)
   * @private
   */
  static #saveCacheToStorage() {
    try {
      // Only cache essential device data to avoid quota exceeded error
      const lightweightCache = {
        devices: {
          data: {
            success: this.#cache.devices?.data?.success,
            data: this.#cache.devices?.data?.data?.map(device => ({
              // Only store essential fields
              id: device.id,
              friendlyName: device.friendlyName,
              device_UUID: device.device_UUID,
              status: device.status,
              locationId: device.locationId,
              deviceTypeId: device.deviceTypeId,
              lastSeen: device.lastSeen,
              // Skip large fields like logs, history, etc.
            })),
            meta: this.#cache.devices?.data?.meta
          },
          status: this.#cache.devices?.status,
          statusText: this.#cache.devices?.statusText
        },
        timestamp: this.#cache.timestamp,
        ttl: this.#cache.ttl
      };

      const cacheStr = JSON.stringify(lightweightCache);
      const cacheSizeKB = (cacheStr.length / 1024).toFixed(2);

      localStorage.setItem(this.#CACHE_KEY, cacheStr);
      console.log(`[DeviceService] Saved cache to localStorage (${cacheSizeKB} KB)`);
    } catch (error) {
      console.error('[DeviceService] Failed to save cache to localStorage:', error);
      // If localStorage is full, clear it and try again with even more minimal data
      try {
        localStorage.removeItem(this.#CACHE_KEY);
        console.warn('[DeviceService] Cleared old cache due to quota error');
      } catch (e) {
        console.error('[DeviceService] Cannot clear localStorage:', e);
      }
    }
  }

  /**
   * Check if cache is valid
   * @private
   * @returns {boolean} True if cache is valid
   */
  static #isCacheValid() {
    // Try to load from localStorage if memory cache is empty
    if (!this.#cache.devices || !this.#cache.timestamp) {
      this.#loadCacheFromStorage();
    }

    if (!this.#cache.devices || !this.#cache.timestamp) {
      return false;
    }
    const now = Date.now();
    return (now - this.#cache.timestamp) < this.#cache.ttl;
  }

  /**
   * Get cache timestamp (public method)
   * @returns {number|null} Cache timestamp or null
   */
  static getCacheTimestamp() {
    return this.#cache.timestamp;
  }

  /**
   * Fetch all devices with optional filtering and caching
   * Handles pagination automatically - fetches all pages if API paginates
   * @param {Object} options - Options object
   * @param {Object} options.filters - Query parameters for filtering devices
   * @param {boolean} options.useCache - Whether to use cache (default: true)
   * @param {boolean} options.forceRefresh - Force refresh even if cache is valid
   * @returns {Promise<Array>} Array of device objects
   */
  static async getAllDevices({ filters = {}, useCache = true, forceRefresh = false, enhanced = false } = {}) {
    try {
      // Return cached data if available and valid
      if (useCache && !forceRefresh && this.#isCacheValid()) {
        console.log('[DeviceService] Returning cached devices (age:', Math.round((Date.now() - this.#cache.timestamp) / 1000), 'seconds)');
        return this.#cache.devices;
      }

      const cacheAge = this.#cache.timestamp ? Math.round((Date.now() - this.#cache.timestamp) / 1000) : 'none';
      console.log('[DeviceService] Fetching devices from API (cache age:', cacheAge, 'seconds, forceRefresh:', forceRefresh, ')');

      const startTime = performance.now();

      // Use centralized API client which handles authentication automatically
      // Start with first page, API limit is 1000 devices per page
      const params = {
        limit: filters.limit || 8000, // Request 8000, API caps at 1000
        page: 1,
        ...filters
      };

      // Remove 'enhanced' from params as API doesn't recognize it
      delete params.enhanced;

      // Fetch first page
      const firstResponse = await devicesApi.getAll(params);
      let allDevices = firstResponse?.data?.data || [];
      const meta = firstResponse?.data?.meta || {};

      console.log('[DeviceService] First page response:', {
        count: allDevices.length,
        totalItems: meta.totalItems,
        totalPages: meta.totalPages,
        limit: meta.limit
      });

      // If there are more pages, fetch them all
      if (meta.totalPages && meta.totalPages > 1) {
        console.log(`[DeviceService] Fetching remaining ${meta.totalPages - 1} pages...`);

        const pagePromises = [];
        for (let page = 2; page <= meta.totalPages; page++) {
          pagePromises.push(
            devicesApi.getAll({ ...params, page })
          );
        }

        const additionalResponses = await Promise.all(pagePromises);

        for (const response of additionalResponses) {
          const pageDevices = response?.data?.data || [];
          allDevices = allDevices.concat(pageDevices);
        }

        console.log(`[DeviceService] Fetched all pages, total devices: ${allDevices.length}`);
      }

      const fetchTime = performance.now() - startTime;
      console.log(`[DeviceService] API fetch completed in ${fetchTime.toFixed(0)}ms, total: ${allDevices.length} devices`);

      // Build response object with all devices
      const response = {
        ...firstResponse,
        data: {
          ...firstResponse?.data,
          data: allDevices,
          meta: {
            ...meta,
            totalItems: allDevices.length,
            totalPages: 1
          }
        }
      };

      // Cache the response in memory (full data)
      this.#cache.devices = response;
      this.#cache.timestamp = Date.now();

      // Persist lightweight cache to localStorage for page reload survival
      // This runs async to not block the response
      try {
        this.#saveCacheToStorage();
      } catch (error) {
        console.warn('[DeviceService] Cache save failed, but continuing:', error);
      }

      return response;
    } catch (error) {
      this.#handleError(error, 'Failed to fetch devices');
    }
  }

  /**
   * Get a specific device by ID
   * @param {string} deviceId - Device identifier
   * @returns {Promise<Object>} Device object
   */
  static async getDeviceById(deviceId) {
    try {
      const response = await devicesApi.getById(deviceId);
      return response.data;
    } catch (error) {
      this.#handleError(error, `Failed to fetch device ${deviceId}`);
    }
  }

  /**
   * Create a new device
   * @param {Object} deviceData - Device creation payload
   * @returns {Promise<Object>} Created device object
   */
  static async createDevice(deviceData) {
    try {
      const response = await devicesApi.create(deviceData);
      return response.data;
    } catch (error) {
      this.#handleError(error, 'Failed to create device');
    }
  }

  /**
   * Update an existing device
   * @param {string} deviceId - Device identifier
   * @param {Object} updateData - Device update payload
   * @returns {Promise<Object>} Updated device object
   */
  static async updateDevice(deviceId, updateData) {
    try {
      const response = await devicesApi.update(deviceId, updateData);
      return response.data;
    } catch (error) {
      this.#handleError(error, `Failed to update device ${deviceId}`);
    }
  }

  /**
   * Delete a device
   * @param {string} deviceId - Device identifier
   * @returns {Promise<void>}
   */
  static async deleteDevice(deviceId) {
    try {
      await devicesApi.delete(deviceId);
    } catch (error) {
      this.#handleError(error, `Failed to delete device ${deviceId}`);
    }
  }

  /**
   * Execute an action on a device
   * @param {string} deviceId - Device identifier
   * @param {string} action - Action to execute
   * @param {Object} params - Action parameters
   * @returns {Promise<Object>} Action result
   */
  static async executeDeviceAction(deviceId, action, params = {}) {
    try {
      // TODO: Add executeAction to devicesApi in apiClient.js
      // For now, return a placeholder
      console.warn('[DeviceService] executeDeviceAction not implemented in apiClient');
      return { success: false, message: 'Action execution not yet implemented' };
    } catch (error) {
      this.#handleError(error, `Failed to execute action ${action} on device ${deviceId}`);
    }
  }

  /**
   * Get device status history
   * @param {string} deviceId - Device identifier
   * @param {Object} timeRange - Time range for history
   * @returns {Promise<Array>} Array of status records
   */
  static async getDeviceStatusHistory(deviceId, timeRange = {}) {
    try {
      // TODO: Add getStatusHistory to devicesApi in apiClient.js
      // For now, return empty array
      console.warn('[DeviceService] getDeviceStatusHistory not implemented in apiClient');
      return [];
    } catch (error) {
      this.#handleError(error, `Failed to fetch status history for device ${deviceId}`);
    }
  }

  /**
   * Get devices by location
   * @param {string} locationId - Location identifier
   * @returns {Promise<Array>} Array of device objects
   */
  static async getDevicesByLocation(locationId) {
    try {
      const response = await devicesApi.getByLocation(locationId);
      return response.data;
    } catch (error) {
      this.#handleError(error, `Failed to fetch devices for location ${locationId}`);
    }
  }

  /**
   * Fetch only device statuses (lightweight endpoint)
   * This should be called frequently to update status without re-fetching full device data
   * @returns {Promise<Object>} Map of device IDs to status objects
   */
  static async getDeviceStatuses() {
    try {
      // TODO: Add getStatuses to devicesApi in apiClient.js for lightweight status polling
      // For now, return null to disable status polling
      console.warn('[DeviceService] getDeviceStatuses not implemented in apiClient - status polling disabled');
      return null;
    } catch (error) {
      // If status endpoint doesn't exist, fall back to full fetch
      console.warn('[DeviceService] Status endpoint not available');
      return null;
    }
  }

  /**
   * Update device statuses in cache without full re-fetch
   * @param {Object} statusMap - Map of device IDs to status data
   */
  static updateCachedStatuses(statusMap) {
    if (!this.#cache.devices) return;

    const updateDeviceStatus = (device) => {
      const deviceId = device.id || device._id || device.device_UUID;
      if (statusMap[deviceId]) {
        device.status = statusMap[deviceId].status;
        device.last_seen = statusMap[deviceId].last_seen;
        if (device.config) {
          device.config.status = statusMap[deviceId].status;
        }
      }
      // Recursively update children
      if (device.children && device.children.length > 0) {
        device.children.forEach(updateDeviceStatus);
      }
    };

    // Update the cached data structure (now using the full response object)
    const data = this.#cache.devices?.data?.data || this.#cache.devices?.data || [];
    if (Array.isArray(data)) {
      data.forEach(updateDeviceStatus);
    }
  }

  /**
   * Invalidate device cache
   */
  static invalidateCache() {
    console.log('[DeviceService] Cache invalidated');
    this.#cache.devices = null;
    this.#cache.timestamp = null;
    // Also clear from localStorage
    try {
      localStorage.removeItem(this.#CACHE_KEY);
    } catch (error) {
      console.error('[DeviceService] Failed to clear cache from localStorage:', error);
    }
  }

  /**
   * Start polling device statuses
   * @param {Function} callback - Callback function to update UI with new statuses
   * @param {number} interval - Polling interval in milliseconds (default: 30 seconds)
   */
  static startStatusPolling(callback, interval = 30000) {
    // Clear existing interval if any
    this.stopStatusPolling();

    console.log(`[DeviceService] Starting status polling every ${interval / 1000}s`);

    this.#statusPollingInterval = setInterval(async () => {
      try {
        const statuses = await this.getDeviceStatuses();
        if (statuses) {
          this.updateCachedStatuses(statuses);
          if (callback) callback(statuses);
        }
      } catch (error) {
        console.error('[DeviceService] Status polling error:', error);
      }
    }, interval);
  }

  /**
   * Stop polling device statuses
   */
  static stopStatusPolling() {
    if (this.#statusPollingInterval) {
      console.log('[DeviceService] Stopping status polling');
      clearInterval(this.#statusPollingInterval);
      this.#statusPollingInterval = null;
    }
  }

  /**
   * Handle API errors
   * @private
   * @param {Error} error - Error object
   * @param {string} defaultMessage - Default error message
   * @throws {Error} Enhanced error object
   */
  static #handleError(error, defaultMessage) {
    // Log detailed error info for debugging
    console.error('[DeviceService] API Error:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      responseData: error.response?.data,
      url: error.config?.url,
      isNetworkError: !error.response,
      isTimeout: error.code === 'ECONNABORTED'
    });

    const errorMessage = error.response?.data?.message || error.message || defaultMessage;
    const enhancedError = new Error(errorMessage);
    enhancedError.originalError = error;
    enhancedError.statusCode = error.response?.status;
    enhancedError.isNetworkError = !error.response;
    throw enhancedError;
  }
}

export default DeviceService;